import { Button, Collapse, RotatingChevron } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import { useEffect } from "react";
import { buttonRoot, collapseRoot, contentRoot } from "./ContentDrawer.style";

const ButtonWithTrackEvent = withTrackEvent(Button);

interface ContentDrawerProps {
  title: string;
  hideContent?: boolean;
  isCollapsed?: boolean;
  children: React.ReactNode;
}

const ContentDrawerComponent = ({
  title,
  children,
  hideContent = false,
  isCollapsed = true,
}: ContentDrawerProps) => {
  const { isHidden, onClick, setIsHidden } = Collapse.useCollapse({
    isHidden: isCollapsed,
  });
  // Sync internal state with isCollapsed prop from parent
  useEffect(() => {
    setIsHidden(isCollapsed);
  }, [isCollapsed]);

  const CustomTrigger = () => (
    <ButtonWithTrackEvent
      __cssFor={{ root: buttonRoot }}
      actionName={actionNames.hide}
      objectType={objectTypes.content}
      ariaProps={{
        "aria-controls": "content-drawer",
        "aria-expanded": !isHidden,
      }}
      flavor={Button.flavors.polarMuted}
      onClick={onClick}
      dataProps={{
        "data-testid": "content-drawer",
        "data-qa-selector": `content_drawer-${isHidden ? "show" : "hide"}`,
      }}
      streamProperties={{
        description: `${isHidden ? "Show" : "Hide"} content drawer`,
      }}
    >
      <h3>{title}</h3>
      <RotatingChevron isDown={!isHidden} timeout={300} />
    </ButtonWithTrackEvent>
  );

  if (hideContent) {
    return (
      <div css={contentRoot} data-testid="content-drawer-static">
        <h3>{title}</h3>
      </div>
    );
  }

  return (
    <Collapse
      isHidden={isHidden}
      renderCustomTrigger={CustomTrigger}
      __cssFor={{ root: collapseRoot }}
    >
      {children}
    </Collapse>
  );
};

export default ContentDrawerComponent;
